import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Stack, TextField, MenuItem, Typography, Chip, Paper } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { api } from '../api/client';
import type { AccessEventDto, PageResponse } from '../api/types';

function isoDaysAgo(d: number): string { const dt = new Date(); dt.setDate(dt.getDate() - d); return dt.toISOString().slice(0, 10); }
function todayIso(): string { return new Date().toISOString().slice(0, 10); }

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'PROCESSED', label: 'Procesado' },
  { value: 'OVERRIDDEN', label: 'Override manual' },
  { value: 'MANUAL_REVIEW', label: 'Revisión manual' },
  { value: 'FAILED', label: 'Fallido' },
];

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'ENTRY', label: 'Entrada' },
  { value: 'EXIT', label: 'Salida' },
  { value: 'UNRESOLVED', label: 'No resuelto' },
];

export function HistoryPage() {
  const nav = useNavigate();
  const [from, setFrom] = useState(isoDaysAgo(7));
  const [to, setTo] = useState(todayIso());
  const [plate, setPlate] = useState('');
  const [status, setStatus] = useState('');
  const [eventType, setEventType] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const params = {
    from: `${from}T00:00:00Z`,
    to: `${to}T23:59:59Z`,
    plate: plate || undefined,
    status: status || undefined,
    eventType: eventType || undefined,
    page: pagination.page,
    size: pagination.pageSize,
    sort: 'capturedAt,desc',
  };

  const { data, isLoading } = useQuery({
    queryKey: ['access-events', params],
    queryFn: () => api.get<PageResponse<AccessEventDto>>('/access-events', { params }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const cols: GridColDef<AccessEventDto>[] = [
    { field: 'capturedAt', headerName: 'Fecha/hora', flex: 1.2,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleString() : '' },
    { field: 'eventType', headerName: 'Tipo', flex: 0.7,
      renderCell: (p) => p.value ? (
        <Chip size="small" label={p.value === 'ENTRY' ? 'Entrada' : p.value === 'EXIT' ? 'Salida' : 'No resuelto'}
          color={p.value === 'ENTRY' ? 'success' : p.value === 'EXIT' ? 'info' : 'warning'} />
      ) : null },
    { field: 'detectedPlate', headerName: 'Placa leída', flex: 0.9,
      renderCell: (p) => p.value
        ? <Chip size="small" label={p.value} sx={{ fontFamily: 'monospace' }} />
        : <Typography variant="caption" color="text.secondary">(no legible)</Typography> },
    { field: 'vehiclePlate', headerName: 'Camioneta', flex: 0.9,
      valueGetter: (_, r) => r.vehicle?.plateDisplay ?? '',
      renderCell: (p) => p.value
        ? <Chip size="small" label={p.value} sx={{ fontFamily: 'monospace' }} variant="outlined" />
        : <Typography variant="caption" color="text.secondary">—</Typography> },
    { field: 'driverName', headerName: 'Chofer', flex: 1,
      valueGetter: (_, r) => r.vehicle?.driverName ?? '' },
    { field: 'confidence', headerName: 'Confianza', flex: 0.6,
      valueFormatter: (v) => v != null ? Number(v).toFixed(2) : '—' },
    { field: 'status', headerName: 'Status', flex: 0.9,
      renderCell: (p) => {
        const v = p.value as string;
        const color = v === 'PROCESSED' ? 'success'
          : v === 'OVERRIDDEN' ? 'primary'
          : v === 'MANUAL_REVIEW' ? 'warning'
          : v === 'FAILED' ? 'error' : 'default';
        return <Chip size="small" label={v} color={color} />;
      } },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Historial</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Todas las lecturas de placa (entradas, salidas, no resueltas). Click en una fila para ver detalle.
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField label="Desde" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Hasta" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Placa contiene" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} sx={{ minWidth: 160 }} />
          <TextField select label="Tipo" value={eventType} onChange={(e) => setEventType(e.target.value)} sx={{ minWidth: 140 }}>
            {EVENT_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
            {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Stack>
      </Paper>
      <Box sx={{ height: 640, bgcolor: 'background.paper' }}>
        <DataGrid<AccessEventDto>
          rows={data?.content ?? []}
          columns={cols}
          loading={isLoading}
          getRowId={(r) => r.id}
          onRowClick={(p) => nav(`/history/${p.row.id}`)}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={data?.totalElements ?? 0}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  );
}
